import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  BillingCycle,
  ISubscriptionRepository,
  ManualPaymentInstructions,
  ManualPaymentMethod,
  ManualSubscriptionRequest,
  PlanCode,
  PlanUsageSnapshot,
  SubscriptionEntitlementPlan,
  SubmitManualSubscriptionRequestInput,
  Subscription,
} from '../../../interfaces/src';

interface SubscriptionRow {
  tenant_id: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  plan: SubscriptionEntitlementPlan;
  billing_cycle: BillingCycle;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  payment_provider: 'flouci' | 'konnect' | null;
  payment_provider_customer_id: string | null;
  payment_provider_subscription_id: string | null;
  access_source?: Subscription['accessSource'];
  grant_starts_at?: string | null;
  grant_ends_at?: string | null;
  grant_lifetime?: boolean;
  grant_reason?: string | null;
  grant_note?: string | null;
  grant_granted_at?: string | null;
  grant_revoked_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface ManualRequestRow {
  id: string;
  tenant_id: string;
  requested_by: string;
  plan: PlanCode;
  billing_cycle: BillingCycle;
  amount_millimes: number;
  payment_method: ManualPaymentMethod;
  proof_path: string;
  customer_note: string | null;
  status: ManualSubscriptionRequest['status'];
  review_note: string | null;
  reviewed_at: string | null;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
}

export class SupabaseSubscriptionRepository implements ISubscriptionRepository {
  constructor(private supabase: SupabaseClient) {}

  async getCurrent(): Promise<Subscription | null> {
    // Same admin trap as tenants-repository.getCurrent(): the RLS policy is
    // `tenant_id = current_user_tenant_id() OR is_admin()`, so a platform
    // admin reads EVERY tenant's row and a bare limit-1 returns whichever
    // one Postgres picks — the billing page then displays (and gates on) a
    // stranger's subscription. Scope explicitly to the resolved tenant so
    // admins and regular users converge on the tenant the checkout edge
    // function will actually evaluate.
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data: tenantId, error: tenantError } = await (
      this.supabase as unknown as SbAny
    ).rpc('current_user_tenant_id', {});
    // Transport/auth/RPC failure is not an authoritative "no tenant" result.
    // Propagate it so the entitlement UI can render a neutral retry state
    // instead of converting uncertainty into a commercial paywall.
    if (tenantError) throw tenantError;
    if (!tenantId) return null;
    const { data, error } = await this.supabase
      .from('tenant_subscriptions')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSubscription(data as SubscriptionRow) : null;
  }

  async ensureForCurrent(trialDays: number): Promise<Subscription> {
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{
        data: SubscriptionRow | null;
        error: { message: string } | null;
      }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'ensure_subscription_for_current_user',
      { p_trial_days: trialDays },
    );
    if (error) throw error;
    if (!data)
      throw new Error('ensure_subscription_for_current_user returned no row');
    return mapSubscription(data);
  }

  async cancelCurrent(): Promise<Subscription> {
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{
        data: SubscriptionRow | null;
        error: { message: string } | null;
      }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'cancel_my_subscription',
      {},
    );
    if (error) throw error;
    if (!data) throw new Error('cancel_my_subscription returned no row');
    return mapSubscription(data);
  }

  async getUsage(): Promise<PlanUsageSnapshot> {
    const { data, error } = await this.supabase.rpc('get_my_plan_usage', {});
    if (error) throw error;
    if (!data || typeof data !== 'object')
      throw new Error('plan_usage_unavailable');
    return mapUsage(data as Record<string, unknown>);
  }

  async getManualInstructions(
    cycle: BillingCycle,
    method: ManualPaymentMethod,
  ): Promise<ManualPaymentInstructions> {
    const { data, error } = await this.supabase.rpc(
      'get_manual_subscription_instructions',
      {
        p_billing_cycle: cycle,
        p_payment_method: method,
      },
    );
    if (error) throw error;
    if (!data || typeof data !== 'object')
      throw new Error('manual_payment_instructions_unavailable');
    return data as unknown as ManualPaymentInstructions;
  }

  async listManualRequests(): Promise<ManualSubscriptionRequest[]> {
    const { data, error } = await this.supabase
      .from('subscription_manual_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as unknown as ManualRequestRow[]).map(
      mapManualRequest,
    );
  }

  async submitManualRequest(
    input: SubmitManualSubscriptionRequestInput,
  ): Promise<ManualSubscriptionRequest> {
    const { data, error } = await this.supabase.rpc(
      'submit_manual_subscription_request',
      {
        p_request_id: input.requestId,
        p_plan: input.plan ?? 'commerce',
        p_billing_cycle: input.billingCycle,
        p_payment_method: input.paymentMethod,
        p_proof_path: input.proofPath,
        p_customer_note: input.customerNote ?? null,
        p_amount_millimes: input.amountMillimes ?? null,
      },
    );
    if (error) throw error;
    if (!data) throw new Error('manual_subscription_request_not_created');
    return mapManualRequest(data as unknown as ManualRequestRow);
  }

  async resubmitManualRequest(
    requestId: string,
    proofPath: string,
    customerNote?: string,
  ): Promise<ManualSubscriptionRequest> {
    const { data, error } = await this.supabase.rpc(
      'resubmit_manual_subscription_request',
      {
        p_request_id: requestId,
        p_proof_path: proofPath,
        p_customer_note: customerNote ?? null,
      },
    );
    if (error) throw error;
    if (!data) throw new Error('manual_subscription_request_not_resubmitted');
    return mapManualRequest(data as unknown as ManualRequestRow);
  }
}

function mapSubscription(row: SubscriptionRow): Subscription {
  return {
    tenantId: row.tenant_id,
    status: row.status,
    plan: row.plan,
    billingCycle: row.billing_cycle,
    trialEndsAt: row.trial_ends_at,
    currentPeriodStart: row.current_period_start,
    currentPeriodEnd: row.current_period_end,
    canceledAt: row.canceled_at,
    paymentProvider: row.payment_provider,
    paymentProviderCustomerId: row.payment_provider_customer_id,
    paymentProviderSubscriptionId: row.payment_provider_subscription_id,
    accessSource:
      row.access_source ??
      (row.status === 'trialing' ? 'trial' : 'legacy_unclassified'),
    grantStartsAt: row.grant_starts_at ?? null,
    grantEndsAt: row.grant_ends_at ?? null,
    grantLifetime: row.grant_lifetime ?? false,
    grantReason: row.grant_reason ?? null,
    grantNote: row.grant_note ?? null,
    grantGrantedAt: row.grant_granted_at ?? null,
    grantRevokedAt: row.grant_revoked_at ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapManualRequest(row: ManualRequestRow): ManualSubscriptionRequest {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    requestedBy: row.requested_by,
    plan: row.plan,
    billingCycle: row.billing_cycle,
    amountMillimes: Number(row.amount_millimes),
    paymentMethod: row.payment_method,
    proofPath: row.proof_path,
    customerNote: row.customer_note,
    status: row.status,
    reviewNote: row.review_note,
    reviewedAt: row.reviewed_at,
    activatedAt: row.activated_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/** The RPC hands back a JSON object; coerce defensively so a missing key can
 *  never surface as NaN in a usage meter. */
function mapUsage(row: Record<string, unknown>): PlanUsageSnapshot {
  const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const iso = (v: unknown): string =>
    typeof v === 'string' ? v : new Date().toISOString();

  const members = num(row.members);
  const pending = num(row.pendingInvitations);
  return {
    stores: num(row.stores),
    // Trust the server's total, but recompute if it is missing so an older
    // RPC deployment still meters correctly.
    activeAccounts:
      row.activeAccounts === undefined
        ? members + pending
        : num(row.activeAccounts),
    members,
    pendingInvitations: pending,
    onlineStoreEnabled: row.onlineStoreEnabled === true,
    storageBytes: num(row.storageBytes),
    storefrontVisitsThisMonth: num(row.storefrontVisitsThisMonth),
    periodStart: iso(row.periodStart),
    periodEnd: iso(row.periodEnd),
    measuredAt: iso(row.measuredAt),
  };
}
